import { Component, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../services/admin/admin.service';
import { HelpersService } from '../../shared/servicio/helpers/helpers.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-alianza',
  templateUrl: './alianza.component.html',
  styleUrls: ['./alianza.component.css']
})
export class AlianzaComponent implements OnInit, OnDestroy {

  @ViewChild('createTemplate') createTemplate!: TemplateRef<any>;
  @ViewChild('editTemplate') editTemplate!: TemplateRef<any>;
  @ViewChild('deleteTemplate') deleteTemplate!: TemplateRef<any>;

  // Subject para manejar la destrucción del componente
  private destroy$ = new Subject<void>();

  // Modal references
  modalRef: BsModalRef;
  modalConfig = {
    class: 'modal-dialog-centered modal-app modal-app-lg',
    backdrop: true,
    ignoreBackdropClick: true
  };

  // Form controls - SIMPLIFICADOS
  searchTextFC: FormControl;

  // Formularios
  allianceForm: FormGroup;
  submitted: boolean = false;
  preloader: boolean = false;
  div_error: number = 0;
  div_text_error: string = '';

  // Estados de filtros - SIMPLIFICADOS
  activeFilterState: 'all' | 'active' | 'inactive' = 'all';

  // Lista de alianzas
  list_alliances: any[] = [];
  laboratories: any[] = [];

  // Paginación
  currentPage: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalItems: number = 0;

  // Filtros - SIMPLIFICADOS
  activeFilter: boolean | undefined = undefined;
  searchFilter: string = '';

  // Estados
  showLoading: boolean = false;
  isRefreshing: boolean = false;

  // Para editar/eliminar
  selectedAlliance: any = null;
  isEditMode: boolean = false;

  // Opciones para formularios
  statusOptions = [
    { value: 1, label: 'Activo' },
    { value: 0, label: 'Inactivo' }
  ];

  constructor(
    private allianceService: AdminService,
    public helper: HelpersService,
    private modalService: BsModalService,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    console.log('AlianzaComponent initialized');
    this.initializeFormControls();
    this.initializeForm();
    this.loadLaboratories();
    this.loadAlliances();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // MÉTODO SIMPLIFICADO PARA INICIALIZAR CONTROLES
  initializeFormControls() {
    this.searchTextFC = new FormControl('');
    // Ya no se suscribe automáticamente a los cambios
  }

  // Método para ejecutar búsqueda manualmente
  performSearch() {
    this.searchFilter = this.searchTextFC.value || '';
    this.currentPage = 1;
    this.loadAlliances();
  }

  // Método para manejar Enter en el campo de búsqueda
  onSearchKeyPress(event: any) {
    if (event.key === 'Enter') {
      this.performSearch();
    }
  }

  initializeForm() {
    this.allianceForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      description: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(500)]],
      laboratory_id: ['', [Validators.required]],
      status: [1, [Validators.required]],
      is_multicommercial: [false],
      is_switcheable: [false],
      start_date: ['', [Validators.required]],
      end_date: ['']
    }, { validators: this.dateValidator });
  }

  // Validador personalizado para fechas
  dateValidator(group: FormGroup) {
    const startDate = group.get('start_date').value;
    const endDate = group.get('end_date').value;

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      if (start >= end) {
        return { dateInvalid: true };
      }
    }
    return null;
  }

  get f() {
    return this.allianceForm.controls;
  }

  loadLaboratories() {
    this.allianceService.getLaboratories().subscribe({
      next: (response: any) => {
        console.log('Laboratories response:', response);
        this.laboratories = response || [];
      },
      error: (error) => {
        console.error('Error loading laboratories:', error);
        this.helper.error('Error al cargar laboratorios', error);
      }
    });
  }

  // === MÉTODOS DE FILTROS SIMPLIFICADOS ===

  // Cambiar filtro de estado (botón único)
  toggleActiveFilter() {
    if (this.activeFilterState === 'all') {
      this.activeFilterState = 'active';
      this.activeFilter = true;
    } else if (this.activeFilterState === 'active') {
      this.activeFilterState = 'inactive';
      this.activeFilter = false;
    } else {
      this.activeFilterState = 'all';
      this.activeFilter = undefined;
    }

    this.currentPage = 1;
    this.loadAlliances();
  }

  // Obtener texto del botón de filtro
  getFilterButtonText(): string {
    switch (this.activeFilterState) {
      case 'active':
        return 'ACTIVAS';
      case 'inactive':
        return 'INACTIVAS';
      default:
        return 'TODAS';
    }
  }

  // Obtener clase CSS del botón de filtro
  getFilterButtonClass(): string {
    switch (this.activeFilterState) {
      case 'active':
        return 'btn-success';
      case 'inactive':
        return 'btn-danger';
      default:
        return 'btn-secondary';
    }
  }

  // === MODAL METHODS ===

  openCreateModal() {
    this.isEditMode = false;
    this.selectedAlliance = null;
    this.resetForm();
    this.modalRef = this.modalService.show(this.createTemplate, this.modalConfig);
  }

  openEditModal(alliance: any) {
    this.isEditMode = true;
    this.selectedAlliance = alliance;
    this.loadAllianceData(alliance);
    this.modalRef = this.modalService.show(this.editTemplate, this.modalConfig);
  }

  openDeleteModal(alliance: any) {
    this.selectedAlliance = alliance;
    this.modalRef = this.modalService.show(this.deleteTemplate, this.modalConfig);
  }

  closeModal() {
    this.modalRef.hide();
    this.resetForm();
  }

  resetForm() {
    this.allianceForm.reset();
    this.initializeForm();
    this.submitted = false;
    this.preloader = false;
    this.div_error = 0;
    this.div_text_error = '';
  }

  loadAllianceData(alliance: any) {
    console.log('Loading alliance data for edit:', alliance);

    this.allianceForm.patchValue({
      name: alliance.name,
      description: alliance.description,
      laboratory_id: alliance.laboratory.laboratory_id || '',
      status: alliance.status,
      is_multicommercial: alliance.is_multicommercial,
      is_switcheable: alliance.is_switcheable,
      start_date: this.formatDateForInput(alliance.start_date),
      end_date: alliance.end_date ? this.formatDateForInput(alliance.end_date) : ''
    });
  }

  deleteAlliance() {
    this.preloader = true;

    this.allianceService.deleteAlliance(this.selectedAlliance.id).subscribe({
      next: () => {
        this.preloader = false;
        this.helper.modalSuccess('¡Alianza eliminada exitosamente!');
        this.closeModal();
        this.refreshAlliancesAfterAction();
      },
      error: (error) => {
        console.error('Error deleting alliance:', error);
        this.preloader = false;
        this.helper.error('Error al eliminar alianza', error);
      }
    });
  }

  // === UTILITY METHODS ===

  formatDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };

    return date.toLocaleDateString('es-ES', options);
  }

  formatDateForInput(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // Cambiar página
  onPageChange(page: number) {
    this.currentPage = page;
    this.loadAlliances();
  }

  // Obtener estado de la alianza
  getAllianceStatus(alliance: any): string {
    if (alliance.status === 0) {
      return 'Inactiva';
    }

    if (!alliance.is_active_status) {
      return 'Inactiva';
    }

    const now = new Date();
    const startDate = new Date(alliance.start_date);
    const endDate = alliance.end_date ? new Date(alliance.end_date) : null;

    if (now < startDate) {
      return 'Programada';
    } else if (endDate && now > endDate) {
      return 'Expirada';
    } else {
      return 'Activa';
    }
  }

  // Obtener clase CSS según el estado
  getStatusClass(alliance: any): string {
    const status = this.getAllianceStatus(alliance);
    switch (status) {
      case 'Activa':
        return 'text-success';
      case 'Programada':
        return 'text-warning';
      case 'Expirada':
        return 'text-danger';
      case 'Inactiva':
        return 'text-muted';
      default:
        return 'text-muted';
    }
  }

  // Obtener texto del estado
  getStatusText(status: number): string {
    const statusObj = this.statusOptions.find(s => s.value === status);
    return statusObj ? statusObj.label : 'Desconocido';
  }

  // Obtener nombre del laboratorio
  getLaboratoryName(alliance: any): string {
    return alliance.laboratory.name || 'Sin laboratorio';
  }

  onSubmit() {
    this.submitted = true;
    this.div_error = 0;
    this.div_text_error = '';

    if (this.allianceForm.errors && this.allianceForm.errors['dateInvalid']) {
      this.div_error = 1;
      this.div_text_error = 'La fecha de inicio debe ser anterior a la fecha de fin';
      return;
    }

    if (this.allianceForm.invalid) {
      this.div_error = 1;
      this.div_text_error = 'Por favor complete todos los campos requeridos correctamente';
      return;
    }

    this.preloader = true;

    const formData = this.allianceForm.value;
    const allianceData = {
      name: formData.name,
      description: formData.description,
      laboratory_id: formData.laboratory_id,
      status: formData.status,
      is_multicommercial: formData.is_multicommercial,
      is_switcheable: formData.is_switcheable,
      start_date: new Date(formData.start_date).toISOString(),
      end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
    };

    const operation = this.isEditMode ?
      this.allianceService.updateAlliance(this.selectedAlliance.id, allianceData) :
      this.allianceService.createAlliance(allianceData);

    operation.subscribe({
      next: (response) => {
        console.log('Operation successful:', response);
        this.preloader = false;
        this.helper.modalSuccess(
          this.isEditMode ? '¡Alianza actualizada exitosamente!' : '¡Alianza creada exitosamente!'
        );
        this.closeModal();
        this.refreshAlliancesAfterAction();
      },
      error: (error) => {
        console.error('Error with alliance:', error);
        this.preloader = false;

        if (error.error && typeof error.error === 'object') {
          const backendErrors = error.error;
          let errorMessage = `Error al ${this.isEditMode ? 'actualizar' : 'crear'} la alianza:<br>`;

          Object.keys(backendErrors).forEach(key => {
            if (Array.isArray(backendErrors[key])) {
              errorMessage += `• ${key}: ${backendErrors[key].join(', ')}<br>`;
            } else {
              errorMessage += `• ${key}: ${backendErrors[key]}<br>`;
            }
          });

          this.div_error = 1;
          this.div_text_error = errorMessage;
        } else {
          this.div_error = 1;
          this.div_text_error = `Error al ${this.isEditMode ? 'actualizar' : 'crear'} la alianza`;
        }
      }
    });
  }

  // Método para forzar la recarga de alianzas
  private forceRefreshAlliances() {
    console.log('Forcing refresh of alliances...');

    // Resetear estados que podrían bloquear la recarga
    this.showLoading = false;
    this.isRefreshing = false;

    // Forzar la recarga inmediatamente
    this.loadAlliances();
  }

  // Método mejorado para loadAlliances (reemplaza el existente)
  loadAlliances() {
    console.log('Loading alliances...', {
      showLoading: this.showLoading,
      isRefreshing: this.isRefreshing,
      currentPage: this.currentPage,
      searchFilter: this.searchFilter,
      activeFilter: this.activeFilter
    });

    // Solo bloquear si ya está cargando (pero no si está refrescando)
    if (this.showLoading) {
      console.log('Already loading, skipping...');
      return;
    }

    this.showLoading = true;

    this.allianceService.listAlliances(
      this.currentPage,
      this.pageSize,
      this.activeFilter,
      this.searchFilter
    ).subscribe({
      next: (response: any) => {
        console.log('Alliances loaded successfully:', response);

        if (Array.isArray(response)) {
          this.list_alliances = response;
          this.totalItems = response.length;
          this.totalPages = 1;
        } else {
          this.list_alliances = response.results || [];
          this.totalItems = response.count || 0;
          this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        }

        this.list_alliances = this.list_alliances.map(alliance => ({
          ...alliance,
          date_created: this.formatDate(alliance.date_created),
          date_modified: this.formatDate(alliance.date_modified)
        }));

        this.showLoading = false;
        this.isRefreshing = false;

        console.log('Alliances list updated:', this.list_alliances.length, 'items');
      },
      error: (error) => {
        console.error('Error loading alliances:', error);
        this.helper.error('Error al cargar alianzas', error);
        this.showLoading = false;
        this.isRefreshing = false;
      }
    });
  }

  // Método alternativo si el anterior no funciona
  private refreshAlliancesAfterAction() {
    // Resetear completamente los estados
    this.showLoading = false;
    this.isRefreshing = false;

    // Pequeño delay para asegurar que el backend procesó el cambio
    setTimeout(() => {
      this.forceRefreshAlliances();
    }, 100);
  }
}
